// ─── Research Lab ─────────────────────────────────────────────────────────────
// True AI-powered academic research laboratory.
// Generates synthetic datasets → runs REAL statistics → generates real charts →
// passes computed outputs to Claude for doctoral-level interpretation.
// Claude NEVER invents numbers. All statistics are genuinely computed.

import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2, Play, Download, Loader2, FlaskConical, FileText, ShieldAlert, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { generateDataset, datasetToCSV, csvToRows, inferVariables } from '../../utils/research/dataGenerator'
import { computeDescriptiveStats, computeCorrelationMatrix, computeOLS, computeCronbachAlpha, computeOneWayANOVA } from '../../utils/research/statisticsEngine'
import { correlationHeatmapSVG, coefficientPlotSVG, histogramSVG, residualsPlotSVG, downloadSVG } from '../../utils/research/chartEngine'
import { buildFindingsSection } from '../../utils/research/interpretationEngine'
import { runFullPipeline, loadDataset, loadResults, loadCharts } from '../../utils/research/researchEngine'
import { DOMAIN_PROFILES, DOMAIN_KEYS, getDomainProfile } from '../../utils/research/domainRealismProfiles'
import { applyTheoryConstraints, describeConstraints } from '../../utils/research/theoryConstraintEngine'
import { validateStatisticalPlausibility } from '../../utils/research/statisticalPlausibilityValidator'
import { recommendPipeline } from '../../utils/research/methodologicalRestraintEngine'
import { generateMixedMethodTensions, generateQualitativeThemes } from '../../utils/research/mixedMethodTensionEngine'
import { buildEmpiricalNarrative } from '../../utils/research/empiricalNarrativeEngine'
import { renderOLSTable, renderDescriptiveTable, renderCorrelationTable, renderCronbachTable } from '../../utils/research/apaTableRenderer'
import { validateResearchAuthenticity, quickJudgment } from '../../utils/research/researchJudgmentEngine'
import toast from 'react-hot-toast'

const VARIABLE_TYPES = [
  { value: 'likert5', label: 'Likert 5-pt' },
  { value: 'likert7', label: 'Likert 7-pt' },
  { value: 'continuous', label: 'Continuous' },
  { value: 'binary', label: 'Binary (0/1)' },
  { value: 'count', label: 'Count' },
]
const VARIABLE_ROLES = [
  { value: 'iv', label: 'Independent (IV)' },
  { value: 'dv', label: 'Dependent (DV)' },
  { value: 'control', label: 'Control' },
  { value: 'moderator', label: 'Moderator' },
]
const DIRECTIONS = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'unknown', label: 'Unknown' },
]
const STRENGTHS = [
  { value: 'strong', label: 'Strong' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'weak', label: 'Weak' },
  { value: 'none', label: 'None (control)' },
]

const DOMAIN_LABELS = {
  public_policy: 'Public Policy',
  marketing: 'Marketing',
  education: 'Education',
  healthcare: 'Healthcare',
  finance: 'Finance',
  human_resources: 'Human Resources',
  sustainability: 'Sustainability',
  operations: 'Operations Management',
  technology_adoption: 'Technology Adoption',
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Panel({ title, children, accent, bg }) {
  return (
    <div style={{ background: bg || '#fff', border: `1px solid ${accent || '#DDE4EF'}`, borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
      {title && <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>{title}</h3>}
      {children}
    </div>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '0.4rem 1rem', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : '#64748b' }}
    >
      {children}
    </button>
  )
}

function ScoreRing({ score, size = 72, label }) {
  const r = (size - 8) / 2
  const circ = 2 * Math.PI * r
  const filled = circ * ((score ?? 0) / 100)
  const color = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : '#dc2626'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={8} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize={size < 72 ? 13 : 16} fontWeight={700} fill={color}>
          {score ?? '—'}
        </text>
      </svg>
      {label && <span style={{ fontSize: 10, color: '#64748b', textAlign: 'center' }}>{label}</span>}
    </div>
  )
}

function SeverityBadge({ severity }) {
  const cfg = {
    high:   { bg: '#fef2f2', color: '#dc2626', label: 'High' },
    medium: { bg: '#fffbeb', color: '#d97706', label: 'Medium' },
    low:    { bg: '#f0fdf4', color: '#059669', label: 'Low' },
  }
  const c = cfg[severity] || cfg.low
  return (
    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: c.bg, color: c.color, fontWeight: 700 }}>
      {c.label}
    </span>
  )
}

function SVGViewer({ svg, title, chartType }) {
  if (!svg) return null
  return (
    <div style={{ border: '1px solid #DDE4EF', borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{title}</span>
        <button
          onClick={() => downloadSVG(svg, `${chartType || 'chart'}.svg`)}
          style={{ fontSize: 10, color: '#2563eb', background: 'none', border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
        >
          ↓ SVG
        </button>
      </div>
      <div dangerouslySetInnerHTML={{ __html: svg }} style={{ maxWidth: '100%', overflowX: 'auto' }} />
    </div>
  )
}

function TensionCard({ tension }) {
  const TYPE_COLORS = {
    IMPLEMENTATION_GAP: '#dc2626',
    LATENT_SALIENCE: '#7c3aed',
    PERCEPTUAL_OPTIMISM: '#d97706',
    HETEROGENEOUS_EFFECTS: '#2563eb',
    INFORMAL_DYNAMICS: '#059669',
  }
  const color = TYPE_COLORS[tension.type] || '#64748b'
  return (
    <div style={{ border: `1px solid ${color}30`, borderLeft: `4px solid ${color}`, borderRadius: 8, padding: '1rem', marginBottom: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{tension.label}</span>
        <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', padding: '1px 7px', borderRadius: 99 }}>{tension.type}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={{ background: '#f0fdf4', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#059669', margin: '0 0 3px' }}>QUANTITATIVE</p>
          <p style={{ fontSize: 11, color: '#334155', margin: 0 }}>{tension.quantSummary}</p>
        </div>
        <div style={{ background: '#fef3c7', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#d97706', margin: '0 0 3px' }}>QUALITATIVE</p>
          <p style={{ fontSize: 11, color: '#334155', margin: 0 }}>{tension.qualSummary}</p>
        </div>
      </div>
      {tension.thematicQuotes && (
        <div style={{ marginBottom: 8 }}>
          {tension.thematicQuotes.slice(0, 2).map((q, i) => (
            <p key={i} style={{ fontSize: 11, color: '#475569', fontStyle: 'italic', borderLeft: '3px solid #DDE4EF', paddingLeft: 8, margin: '4px 0' }}>{q}</p>
          ))}
        </div>
      )}
      <p style={{ fontSize: 11, color: '#334155', lineHeight: 1.65, margin: 0 }}>{tension.tensionNarrative}</p>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ResearchLabPage() {
  const { id: thesisId } = useParams()
  const [thesis, setThesis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('configure')
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(null)
  const [interpreting, setInterpreting] = useState(false)
  const interpretRef = useRef('')

  // Config state
  const [variables, setVariables] = useState([])
  const [sampleSize, setSampleSize] = useState(120)
  const [selectedAnalyses, setSelectedAnalyses] = useState(['descriptive', 'correlation', 'ols'])
  const [domain, setDomain] = useState('public_policy')

  // Results state
  const [dataset, setDataset] = useState(null)
  const [results, setResults] = useState({})
  const [charts, setCharts] = useState({})
  const [interpretation, setInterpretation] = useState('')

  // Epistemic realism state
  const [plausibility, setPlausibility] = useState(null)
  const [tensions, setTensions] = useState([])
  const [qualThemes, setQualThemes] = useState(null)
  const [narrative, setNarrative] = useState(null)
  const [authenticity, setAuthenticity] = useState(null)
  const [constraintNotes, setConstraintNotes] = useState([])
  const [pipelineRecs, setPipelineRecs] = useState(null)

  // Load thesis + existing data
  useEffect(() => {
    if (!thesisId) return
    const load = async () => {
      const { data: t } = await supabase.from('theses').select('*').eq('id', thesisId).maybeSingle()
      setThesis(t)

      if (t) {
        const suggested = inferVariables(t)
        setVariables(suggested)
      }

      const [dbDataset] = await Promise.all([
        loadDataset(thesisId).catch(() => null),
      ])

      if (dbDataset) {
        const rows = csvToRows(dbDataset.csv_data)
        setDataset({ rows, variables: dbDataset.variables_config, sampleSize: dbDataset.row_count })
        setVariables(dbDataset.variables_config)
      }

      setLoading(false)
    }
    load()
  }, [thesisId])

  const dvVar = variables.find(v => v.role === 'dv')
  const ivVars = variables.filter(v => v.role === 'iv')
  const domainProfile = getDomainProfile(domain)

  // Variable editor
  const addVariable = () => setVariables(prev => [...prev, {
    name: `var_${prev.length + 1}`, label: `Variable ${prev.length + 1}`,
    type: 'likert5', role: 'iv', direction: 'positive', strength: 'moderate',
  }])

  const updateVariable = (idx, field, value) => {
    setVariables(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v))
  }

  const removeVariable = (idx) => setVariables(prev => prev.filter((_, i) => i !== idx))

  // Run pipeline
  const handleRun = async () => {
    if (variables.length < 2) { toast.error('Add at least 2 variables'); return }
    if (!dvVar) { toast.error('Mark one variable as Dependent (DV)'); return }

    setRunning(true)
    setResults({})
    setCharts({})
    setInterpretation('')
    interpretRef.current = ''
    setPlausibility(null)
    setTensions([])
    setQualThemes(null)
    setNarrative(null)
    setAuthenticity(null)
    setConstraintNotes([])

    try {
      // Step 1: Get methodological restraint recommendations
      const recs = recommendPipeline({ domain, sampleSize, variables, thesisType: thesis?.thesis_type })
      setPipelineRecs(recs)
      if (recs.overproductionWarnings?.length > 0) {
        recs.overproductionWarnings.forEach(w => toast(w, { icon: '⚠️' }))
      }

      // Step 2: Apply theory constraints before data generation
      setProgress({ message: 'Applying domain theory constraints…', percent: 5 })
      const constrainedVars = applyTheoryConstraints(variables, domain, thesis)
      const notes = describeConstraints(constrainedVars, domain)
      setConstraintNotes(notes)

      const ivNames = constrainedVars.filter(v => v.role === 'iv').map(v => v.name)

      // Step 3: Run full pipeline with constrained variables
      const { dataset: ds, results: res, charts: ch } = await runFullPipeline(
        thesisId,
        { variables: constrainedVars, sampleSize, dvName: dvVar.name, ivNames, analysisTypes: selectedAnalyses },
        ({ message, percent }) => setProgress({ message, percent })
      )

      setDataset(ds)
      setResults(res)
      setCharts(ch)

      // Step 4: Post-run realism analysis
      setProgress({ message: 'Running plausibility audit…', percent: 92 })

      const paus = validateStatisticalPlausibility(res, domain)
      setPlausibility(paus)

      if (res.ols) {
        const tens = generateMixedMethodTensions(res.ols, domain, thesis)
        setTensions(tens)

        const qt = generateQualitativeThemes(domain, res.ols)
        setQualThemes(qt)

        const narr = buildEmpiricalNarrative({ hypotheses: [], olsResult: res.ols, domain, thesis })
        setNarrative(narr)

        const auth = validateResearchAuthenticity({
          results: res,
          narrative: narr,
          interpretation: '',
          tensions: tens,
          domain,
          variables: constrainedVars,
        })
        setAuthenticity(auth)

        if (paus.plausibilityScore < 65) {
          toast('Plausibility score below 65 — review Quality tab before exporting', { icon: '⚠️' })
        }
        if (auth.authenticityScore >= 80) {
          toast.success('Research output is academically authentic')
        }
      }

      setProgress({ message: 'Complete', percent: 100 })
      setActiveTab('stats')
      toast.success('Analysis complete — real statistics computed')
    } catch (err) {
      toast.error('Pipeline failed: ' + err.message)
      console.error('[ResearchLab]', err)
    } finally {
      setRunning(false)
      setProgress(null)
    }
  }

  // Interpret results
  const handleInterpret = async () => {
    if (!results.ols) { toast.error('Run OLS regression first'); return }

    setInterpreting(true)
    setInterpretation('')
    interpretRef.current = ''

    try {
      await buildFindingsSection(
        [results.descriptive && { matrix: results.descriptive }, results.correlation, results.ols].filter(r => r && (r.coefficients || r.matrix || r.alpha)),
        thesis,
        'Results and Analysis',
        (chunk) => {
          interpretRef.current += chunk
          setInterpretation(prev => prev + chunk)
        }
      )

      // Re-run authenticity with real interpretation text
      if (narrative && tensions) {
        const auth = validateResearchAuthenticity({
          results,
          narrative,
          interpretation: interpretRef.current,
          tensions,
          domain,
          variables,
        })
        setAuthenticity(auth)
      }

      setActiveTab('interpret')
    } catch (err) {
      toast.error('Interpretation failed: ' + err.message)
    } finally {
      setInterpreting(false)
    }
  }

  const copyInterpretation = () => {
    navigator.clipboard.writeText(interpretation)
    toast.success('Copied to clipboard — paste into chapter editor')
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 4rem)', overflow: 'hidden', background: '#f8fafc' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes blink { 0%, 100% { opacity: 1 } 50% { opacity: 0 } } select { font-size: 11px !important; }`}</style>

      {/* ── Sidebar ── */}
      <div style={{ width: 300, flexShrink: 0, background: '#fff', borderRight: '1px solid #DDE4EF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '1rem', borderBottom: '1px solid #DDE4EF' }}>
          <Link to={`/app/thesis/${thesisId}/chapters`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b', textDecoration: 'none', marginBottom: 10 }}>
            <ChevronLeft style={{ width: 12, height: 12 }} /> Chapters
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <FlaskConical style={{ width: 18, height: 18, color: '#7c3aed' }} />
            <h1 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>Research Lab</h1>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{thesis?.title}</p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Domain selector */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Research Domain</label>
            <select
              value={domain}
              onChange={e => setDomain(e.target.value)}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid #DDE4EF', borderRadius: 6, fontSize: 12, color: '#334155', background: '#fff', marginBottom: 7 }}
            >
              {Object.entries(DOMAIN_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
              {[
                ['Expected R²', `${domainProfile.typicalR2Range[0]}–${domainProfile.typicalR2Range[1]}`],
                ['Sig. rate', `~${Math.round(domainProfile.significanceRate * 100)}%`],
                ['Contradiction', domainProfile.qualitativeTensionLevel || '—'],
                ['Sophistication', domainProfile.analysisSophistication || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ background: '#f8fafc', borderRadius: 5, padding: '3px 7px' }}>
                  <p style={{ fontSize: 9, color: '#94a3b8', margin: 0 }}>{k}</p>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#334155', margin: 0 }}>{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pipeline status */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Pipeline Status</p>
            {[
              { label: 'Dataset',         ok: !!dataset },
              { label: 'Descriptive',     ok: !!results.descriptive },
              { label: 'Correlation',     ok: !!results.correlation },
              { label: 'OLS Regression',  ok: !!results.ols },
              { label: 'Cronbach α',      ok: !!results.cronbach },
              { label: 'Plausibility',    ok: !!plausibility },
              { label: 'Mixed-method',    ok: tensions.length > 0 },
              { label: 'Interpretation',  ok: interpretation.length > 100 },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 11 }}>
                <span style={{ color: item.ok ? '#059669' : '#d1d5db', width: 14, textAlign: 'center' }}>{item.ok ? '✓' : '○'}</span>
                <span style={{ color: item.ok ? '#334155' : '#94a3b8' }}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Quality summary (after run) */}
          {authenticity && (
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7 }}>Quality Scores</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 6 }}>
                <ScoreRing score={authenticity.authenticityScore} size={64} label="Authenticity" />
                <ScoreRing score={plausibility?.plausibilityScore} size={64} label="Plausibility" />
              </div>
              <p style={{ fontSize: 10, color: authenticity.ready ? '#059669' : '#d97706', margin: 0, textAlign: 'center', fontWeight: 600 }}>
                {authenticity.ready ? '✓ Ready for export' : '⚠ Review before export'}
              </p>
            </div>
          )}

          {/* Constraint notes (after run) */}
          {constraintNotes.length > 0 && (
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 }}>Theory Constraints Applied</p>
              {constraintNotes.map((note, i) => (
                <p key={i} style={{ fontSize: 10, color: '#64748b', margin: '2px 0', lineHeight: 1.5 }}>· {note}</p>
              ))}
            </div>
          )}

          {/* Sample size */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Sample size: <strong>{sampleSize}</strong></label>
            <input
              type="range" min={30} max={500} step={10}
              value={sampleSize}
              onChange={e => setSampleSize(+e.target.value)}
              style={{ width: '100%', accentColor: '#7c3aed' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94a3b8' }}>
              <span>30</span><span>500</span>
            </div>
          </div>

          {/* Analysis selection */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Include</p>
            {[
              { key: 'descriptive', label: 'Descriptive Stats' },
              { key: 'correlation', label: 'Correlation Matrix' },
              { key: 'ols', label: 'OLS Regression' },
              { key: 'cronbach', label: 'Cronbach Alpha' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', padding: '2px 0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedAnalyses.includes(key)}
                  onChange={e => setSelectedAnalyses(prev =>
                    e.target.checked ? [...prev, key] : prev.filter(k => k !== key)
                  )}
                  style={{ accentColor: '#7c3aed' }}
                />
                {label}
              </label>
            ))}
          </div>

          {/* Methodological recommendations */}
          {pipelineRecs?.exclusionWarnings?.length > 0 && (
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF', background: '#fffbeb' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#d97706', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 }}>Restraint Warnings</p>
              {pipelineRecs.exclusionWarnings.map((w, i) => (
                <p key={i} style={{ fontSize: 10, color: '#92400e', margin: '2px 0', lineHeight: 1.5 }}>⚠ {w}</p>
              ))}
            </div>
          )}
        </div>

        {/* Run buttons */}
        <div style={{ padding: '0.75rem', borderTop: '1px solid #DDE4EF', flexShrink: 0 }}>
          {progress && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ height: 4, background: '#e2e8f0', borderRadius: 99, marginBottom: 4 }}>
                <div style={{ height: '100%', background: '#7c3aed', borderRadius: 99, width: `${progress.percent}%`, transition: 'width 0.3s' }} />
              </div>
              <p style={{ fontSize: 10, color: '#7c3aed', margin: 0 }}>{progress.message}</p>
            </div>
          )}
          <button
            onClick={handleRun}
            disabled={running || variables.length < 2}
            style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: running || variables.length < 2 ? 0.7 : 1 }}
          >
            {running
              ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Computing…</>
              : <><Play style={{ width: 14, height: 14 }} /> Run Analysis</>}
          </button>
          {results.ols && (
            <button
              onClick={handleInterpret}
              disabled={interpreting}
              style={{ width: '100%', marginTop: 6, padding: '0.5rem', borderRadius: 8, border: '1px solid #DDE4EF', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: interpreting ? 0.7 : 1 }}
            >
              {interpreting
                ? <><Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> Interpreting…</>
                : <><FileText style={{ width: 13, height: 13 }} /> Interpret Results</>}
            </button>
          )}
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tabs */}
        <div style={{ background: '#fff', borderBottom: '1px solid #DDE4EF', padding: '0.5rem 1rem', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { key: 'configure',   label: '⚙ Variables' },
            { key: 'data',        label: '🗄 Dataset' },
            { key: 'stats',       label: '📊 Statistics' },
            { key: 'charts',      label: '📈 Charts' },
            { key: 'qualitative', label: '💬 Qualitative' },
            { key: 'quality',     label: '🛡 Quality' },
            { key: 'interpret',   label: '✍ Interpretation' },
          ].map(t => (
            <TabButton key={t.key} active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </TabButton>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>

          {/* ── Configure tab ── */}
          {activeTab === 'configure' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Variable Configuration</h2>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                    Define research variables. Direction and strength will be automatically adjusted by domain-specific theory constraints before data generation.
                  </p>
                </div>
                <button
                  onClick={addVariable}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0.4rem 0.75rem', borderRadius: 7, border: '1px solid #7c3aed', background: '#f5f3ff', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  <Plus style={{ width: 13, height: 13 }} /> Add Variable
                </button>
              </div>

              {/* Domain contradiction preview */}
              {domainProfile.contradictions?.length > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: 11, color: '#78350f' }}>
                  <strong>Domain contradictions active ({DOMAIN_LABELS[domain]}):</strong>{' '}
                  {domainProfile.contradictions.slice(0, 2).join(' · ')}
                </div>
              )}

              {variables.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  <FlaskConical style={{ width: 32, height: 32, margin: '0 auto 0.75rem', opacity: 0.4 }} />
                  <p style={{ margin: 0 }}>No variables yet. Click "Add Variable" or variables will be auto-suggested from your thesis topic.</p>
                </div>
              )}

              {variables.map((v, idx) => (
                <div key={idx} style={{
                  background: v._wasReversed ? '#fff7ed' : v.decoupled ? '#faf5ff' : '#fff',
                  border: `2px solid ${v.role === 'dv' ? '#7c3aed' : v._wasReversed ? '#f97316' : v.role === 'moderator' ? '#f59e0b' : '#DDE4EF'}`,
                  borderRadius: 10, padding: '0.75rem 1rem', marginBottom: 8,
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                    <div>
                      <input
                        value={v.name}
                        onChange={e => updateVariable(idx, 'name', e.target.value.replace(/\s+/g, '_').toLowerCase())}
                        placeholder="variable_name"
                        style={{ width: '100%', padding: '4px 8px', border: '1px solid #DDE4EF', borderRadius: 6, fontSize: 12, marginBottom: 3 }}
                      />
                      <input
                        value={v.label || ''}
                        onChange={e => updateVariable(idx, 'label', e.target.value)}
                        placeholder="Display label"
                        style={{ width: '100%', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, color: '#64748b' }}
                      />
                      {v._wasReversed && <p style={{ fontSize: 9, color: '#f97316', margin: '2px 0 0' }}>⚡ Direction reversed by theory constraint</p>}
                      {v.decoupled && <p style={{ fontSize: 9, color: '#7c3aed', margin: '2px 0 0' }}>⚡ Decoupled (isomorphic mimicry)</p>}
                    </div>
                    <select value={v.type} onChange={e => updateVariable(idx, 'type', e.target.value)} style={selectStyle}>
                      {VARIABLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <select value={v.role} onChange={e => updateVariable(idx, 'role', e.target.value)} style={selectStyle}>
                      {VARIABLE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    {v.role !== 'dv' ? (
                      <>
                        <select value={v.direction} onChange={e => updateVariable(idx, 'direction', e.target.value)} style={selectStyle}>
                          {DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                        <select value={v.strength} onChange={e => updateVariable(idx, 'strength', e.target.value)} style={selectStyle}>
                          {STRENGTHS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </>
                    ) : (
                      <div style={{ gridColumn: 'span 2', fontSize: 11, color: '#7c3aed', padding: '0 8px' }}>
                        Outcome variable
                      </div>
                    )}
                    <button onClick={() => removeVariable(idx)} style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                      <Trash2 style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                </div>
              ))}

              {variables.length > 0 && (
                <div style={{ marginTop: '1rem', background: '#f8fafc', borderRadius: 10, padding: '1rem', fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                  <strong>Realism rules applied:</strong> Non-perfect correlations, ±12% noise on expected strength, 15% chance of contradictory weak findings, 2.8% outlier rate, 1.8% missing values. Some hypotheses will partially fail — that is correct research behaviour for {DOMAIN_LABELS[domain]}.
                </div>
              )}
            </div>
          )}

          {/* ── Data tab ── */}
          {activeTab === 'data' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  {dataset ? `Dataset (N = ${dataset.sampleSize})` : 'No dataset yet'}
                </h2>
                {dataset && (
                  <button
                    onClick={() => {
                      const csv = datasetToCSV(dataset)
                      const blob = new Blob([csv], { type: 'text/csv' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url; a.download = 'research_dataset.csv'
                      a.click(); URL.revokeObjectURL(url)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0.4rem 0.75rem', border: '1px solid #DDE4EF', borderRadius: 7, background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Download style={{ width: 13, height: 13 }} /> Download CSV
                  </button>
                )}
              </div>

              {!dataset && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Configure variables and click Run Analysis to generate the dataset.
                </div>
              )}

              {dataset && (
                <Panel title="First 20 rows">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr>{['id', ...dataset.variables.map(v => v.name)].map(h => (
                          <th key={h} style={{ padding: '5px 8px', background: '#f8fafc', borderBottom: '1px solid #DDE4EF', fontWeight: 700, color: '#334155', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {dataset.rows.slice(0, 20).map(row => (
                          <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            {['id', ...dataset.variables.map(v => v.name)].map(col => (
                              <td key={col} style={{ padding: '4px 8px', color: row[col] == null ? '#dc2626' : '#475569' }}>
                                {row[col] == null ? 'NA' : row[col]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {dataset.sampleSize > 20 && (
                    <p style={{ fontSize: 10, color: '#94a3b8', margin: '6px 0 0' }}>Showing 20 of {dataset.sampleSize} rows</p>
                  )}
                </Panel>
              )}
            </div>
          )}

          {/* ── Statistics tab — APA tables ── */}
          {activeTab === 'stats' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Statistical Results</h2>
                {plausibility && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>Plausibility:</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: plausibility.plausibilityScore >= 70 ? '#059669' : plausibility.plausibilityScore >= 50 ? '#d97706' : '#dc2626' }}>
                      {plausibility.plausibilityScore}/100
                    </span>
                  </div>
                )}
              </div>

              {Object.keys(results).length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Run the analysis pipeline to see real computed statistics here.
                </div>
              )}

              {/* Empirical narrative: RQ linkage */}
              {narrative?.rqLinkage && (
                <Panel title="Research Question Linkage" accent="#7c3aed" bg="#faf5ff">
                  <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.7, margin: 0 }}>{narrative.rqLinkage}</p>
                  {narrative.researchCoherence?.warnings?.map((w, i) => (
                    <div key={i} style={{ marginTop: 8, fontSize: 11, color: '#d97706', background: '#fffbeb', borderRadius: 6, padding: '4px 10px' }}>⚠ {w}</div>
                  ))}
                </Panel>
              )}

              {/* APA Descriptive table */}
              {results.descriptive && (
                <Panel title="Table 1 — Descriptive Statistics (APA 7th Edition)">
                  <div dangerouslySetInnerHTML={{ __html: renderDescriptiveTable(results.descriptive, 1) }} />
                </Panel>
              )}

              {/* APA Correlation table */}
              {results.correlation && (
                <Panel title="Table 2 — Correlations">
                  <div dangerouslySetInnerHTML={{ __html: renderCorrelationTable(results.correlation, results.descriptive, 2) }} />
                </Panel>
              )}

              {/* APA OLS table */}
              {results.ols && (
                <Panel title="Table 3 — OLS Regression (APA 7th Edition)" accent="#2563eb">
                  <div dangerouslySetInnerHTML={{ __html: renderOLSTable(results.ols, 3) }} />

                  {/* Model diagnostics */}
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: 12, flexWrap: 'wrap', padding: '0.5rem', background: '#f8fafc', borderRadius: 6 }}>
                    {results.ols.breusch_pagan && (
                      <div style={{ fontSize: 11, color: results.ols.breusch_pagan.heteroscedastic ? '#dc2626' : '#059669' }}>
                        Breusch-Pagan: LM={results.ols.breusch_pagan.lm_statistic}, p={results.ols.breusch_pagan.pvalue}
                        {results.ols.breusch_pagan.heteroscedastic ? ' ⚠ Heteroscedastic' : ' ✓ Homoscedastic'}
                      </div>
                    )}
                    {results.ols.durbin_watson && (
                      <div style={{ fontSize: 11, color: results.ols.durbin_watson < 1.5 || results.ols.durbin_watson > 2.5 ? '#d97706' : '#64748b' }}>
                        Durbin-Watson: {results.ols.durbin_watson}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#64748b' }}>RMSE: {results.ols.rmse} | AIC: {results.ols.aic} | BIC: {results.ols.bic}</div>
                  </div>
                </Panel>
              )}

              {/* APA Cronbach table */}
              {results.cronbach && (
                <Panel title="Table 4 — Reliability Analysis">
                  <div dangerouslySetInnerHTML={{ __html: renderCronbachTable(results.cronbach, 4) }} />
                </Panel>
              )}

              {/* Hypothesis outcomes */}
              {narrative?.hypothesisOutcomes?.length > 0 && (
                <Panel title="Hypothesis Outcome Summary">
                  <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                    {[
                      { label: 'Supported', count: narrative.supportedCount, color: '#059669' },
                      { label: 'Partial', count: narrative.partiallySupportedCount, color: '#d97706' },
                      { label: 'Not supported', count: narrative.notSupportedCount, color: '#dc2626' },
                    ].map(({ label, count, color }) => (
                      <div key={label} style={{ flex: 1, textAlign: 'center', padding: '0.5rem', background: `${color}10`, borderRadius: 8, border: `1px solid ${color}30` }}>
                        <p style={{ fontSize: 20, fontWeight: 800, color, margin: 0 }}>{count}</p>
                        <p style={{ fontSize: 10, color: '#64748b', margin: 0 }}>{label}</p>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.7, margin: '0 0 0.75rem' }}>{narrative.modelNarrative}</p>
                  {narrative.hypothesisOutcomes.map((h, i) => (
                    <div key={i} style={{
                      padding: '0.6rem 0.75rem', borderRadius: 7, marginBottom: 6,
                      background: h.outcome === 'supported' ? '#f0fdf4' : h.outcome === 'partially_supported' ? '#fffbeb' : '#fef2f2',
                      border: `1px solid ${h.outcome === 'supported' ? '#bbf7d0' : h.outcome === 'partially_supported' ? '#fde68a' : '#fecaca'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>H{h.number}: {h.iv?.replace(/_/g, ' ')}</span>
                        <span style={{
                          fontSize: 10, padding: '1px 7px', borderRadius: 99, fontWeight: 700,
                          background: h.outcome === 'supported' ? '#059669' : h.outcome === 'partially_supported' ? '#d97706' : '#dc2626',
                          color: '#fff',
                        }}>
                          {h.outcome === 'supported' ? 'Supported' : h.outcome === 'partially_supported' ? 'Partial' : 'Not supported'}
                        </span>
                      </div>
                      <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.6, margin: 0 }}>{h.narrative}</p>
                    </div>
                  ))}
                </Panel>
              )}
            </div>
          )}

          {/* ── Charts tab ── */}
          {activeTab === 'charts' && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: '1rem' }}>Generated Charts</h2>

              {Object.keys(charts).length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Run the analysis pipeline to generate publication-quality SVG charts.
                </div>
              )}

              {charts.heatmap && <SVGViewer svg={charts.heatmap} title="Correlation Matrix Heatmap" chartType="heatmap" />}
              {charts.coefficients && <SVGViewer svg={charts.coefficients} title="Regression Coefficients (Forest Plot)" chartType="coefficients" />}
              {charts.residuals && <SVGViewer svg={charts.residuals} title="Residuals vs Fitted" chartType="residuals" />}
              {charts.scatters && Object.entries(charts.scatters).map(([iv, svg]) => (
                <SVGViewer key={iv} svg={svg} title={`${iv} → ${dvVar?.name}`} chartType={`scatter_${iv}`} />
              ))}
              {charts.histograms && Object.entries(charts.histograms).map(([vname, svg]) => (
                <SVGViewer key={vname} svg={svg} title={`Distribution: ${vname}`} chartType={`hist_${vname}`} />
              ))}
            </div>
          )}

          {/* ── Qualitative tab ── */}
          {activeTab === 'qualitative' && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>Mixed-Method Tensions</h2>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: '1.25rem' }}>
                Realistic qualitative–quantitative divergences based on {DOMAIN_LABELS[domain]} domain patterns. Use these to enrich your Discussion chapter.
              </p>

              {tensions.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Run OLS regression to generate mixed-method tensions.
                </div>
              )}

              {tensions.map((t, i) => <TensionCard key={i} tension={t} />)}

              {/* Qualitative themes */}
              {qualThemes && (
                <Panel title="Qualitative Thematic Summary" accent="#7c3aed" bg="#faf5ff">
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', margin: '0 0 5px' }}>Primary themes</p>
                    {qualThemes.themes.map((t, i) => (
                      <p key={i} style={{ fontSize: 11, color: '#334155', margin: '4px 0', lineHeight: 1.65, paddingLeft: 12, borderLeft: '3px solid #c4b5fd' }}>{t}</p>
                    ))}
                  </div>

                  {qualThemes.dominantNarratives?.length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', margin: '0 0 5px' }}>Dominant narratives</p>
                      {qualThemes.dominantNarratives.map((n, i) => (
                        <p key={i} style={{ fontSize: 11, color: '#475569', margin: '3px 0' }}>· {n}</p>
                      ))}
                    </div>
                  )}

                  <div style={{ background: '#ede9fe', borderRadius: 7, padding: '0.6rem 0.75rem' }}>
                    <p style={{ fontSize: 11, color: '#4c1d95', lineHeight: 1.65, margin: 0 }}>{qualThemes.methodologicalNote}</p>
                  </div>
                </Panel>
              )}

              {/* Implications */}
              {narrative?.implications && (
                <Panel title="Research Implications">
                  {[
                    { label: 'Policy implications', items: narrative.implications.policy, color: '#2563eb' },
                    { label: 'Theoretical implications', items: narrative.implications.theoretical, color: '#7c3aed' },
                    { label: 'Methodological implications', items: narrative.implications.methodological, color: '#059669' },
                  ].map(({ label, items, color }) => items?.length > 0 && (
                    <div key={label} style={{ marginBottom: '0.75rem' }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color, margin: '0 0 5px' }}>{label}</p>
                      {items.map((item, i) => (
                        <p key={i} style={{ fontSize: 11, color: '#475569', margin: '4px 0', lineHeight: 1.65, paddingLeft: 10, borderLeft: `3px solid ${color}40` }}>{item}</p>
                      ))}
                    </div>
                  ))}
                </Panel>
              )}
            </div>
          )}

          {/* ── Quality tab ── */}
          {activeTab === 'quality' && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>Research Quality Audit</h2>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: '1.25rem' }}>
                Checks for overproduction, AI-typical patterns, and statistical implausibility before export.
              </p>

              {!authenticity && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Run analysis to generate quality audit.
                </div>
              )}

              {authenticity && (
                <>
                  {/* Score overview */}
                  <Panel title="Overall Scores">
                    <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginBottom: '1rem' }}>
                      <ScoreRing score={authenticity.authenticityScore} size={90} label="Authenticity" />
                      <ScoreRing score={plausibility?.plausibilityScore} size={90} label="Plausibility" />
                    </div>
                    <div style={{
                      padding: '0.75rem 1rem', borderRadius: 8, textAlign: 'center',
                      background: authenticity.ready ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${authenticity.ready ? '#bbf7d0' : '#fecaca'}`,
                    }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: authenticity.ready ? '#059669' : '#dc2626', margin: 0 }}>
                        {authenticity.verdict}
                      </p>
                    </div>
                  </Panel>

                  {/* Judgment issues */}
                  {authenticity.judgmentIssues?.length > 0 && (
                    <Panel title="Judgment Issues" accent="#dc2626">
                      {authenticity.judgmentIssues.map((issue, i) => (
                        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < authenticity.judgmentIssues.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                            <ShieldAlert style={{ width: 14, height: 14, color: issue.severity === 'high' ? '#dc2626' : '#d97706', flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', flex: 1 }}>{issue.criterion}</span>
                            <SeverityBadge severity={issue.severity} />
                          </div>
                          <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.65, margin: '0 0 5px 22px' }}>{issue.message}</p>
                          <p style={{ fontSize: 11, color: '#2563eb', background: '#eff6ff', borderRadius: 5, padding: '4px 10px', margin: '0 0 0 22px' }}>
                            <strong>Recommendation:</strong> {issue.recommendation}
                          </p>
                        </div>
                      ))}
                    </Panel>
                  )}

                  {/* Plausibility warnings */}
                  {plausibility?.warnings?.length > 0 && (
                    <Panel title="Statistical Plausibility Warnings" accent="#d97706">
                      {plausibility.warnings.map((w, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                          <AlertTriangle style={{ width: 13, height: 13, color: w.severity === 'high' ? '#dc2626' : '#d97706', flexShrink: 0, marginTop: 2 }} />
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', margin: '0 0 2px' }}>{w.check}</p>
                            <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>{w.message}</p>
                          </div>
                          <SeverityBadge severity={w.severity} />
                        </div>
                      ))}
                    </Panel>
                  )}

                  {/* Overproduction warnings */}
                  {authenticity.overproductionWarnings?.length > 0 && (
                    <Panel title="Overproduction Warnings" accent="#7c3aed">
                      {authenticity.overproductionWarnings.map((w, i) => (
                        <p key={i} style={{ fontSize: 11, color: '#4c1d95', margin: '3px 0', lineHeight: 1.6 }}>⚡ {w}</p>
                      ))}
                    </Panel>
                  )}

                  {/* Suggested simplifications */}
                  {authenticity.suggestedSimplifications?.length > 0 && (
                    <Panel title="Suggested Simplifications" accent="#059669">
                      {authenticity.suggestedSimplifications.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                          <CheckCircle2 style={{ width: 13, height: 13, color: '#059669', flexShrink: 0, marginTop: 2 }} />
                          <p style={{ fontSize: 11, color: '#334155', margin: 0, lineHeight: 1.6 }}>{s}</p>
                        </div>
                      ))}
                    </Panel>
                  )}

                  {/* No issues */}
                  {authenticity.judgmentIssues?.length === 0 && plausibility?.warnings?.length === 0 && (
                    <Panel accent="#059669" bg="#f0fdf4">
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <CheckCircle2 style={{ width: 20, height: 20, color: '#059669' }} />
                        <p style={{ fontSize: 13, color: '#059669', fontWeight: 600, margin: 0 }}>
                          No quality issues detected. Research output is academically defensible.
                        </p>
                      </div>
                    </Panel>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Interpretation tab ── */}
          {activeTab === 'interpret' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>AI Interpretation</h2>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                    Claude interprets REAL computed outputs. All numbers came from genuine statistical computation — none were invented.
                  </p>
                </div>
                {interpretation && (
                  <button
                    onClick={copyInterpretation}
                    style={{ padding: '0.4rem 0.75rem', border: '1px solid #DDE4EF', borderRadius: 7, background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Copy to Editor
                  </button>
                )}
              </div>

              {!results.ols && (
                <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #DDE4EF', borderRadius: 12, color: '#94a3b8' }}>
                  Run OLS regression first, then click "Interpret Results" in the sidebar.
                </div>
              )}

              {results.ols && !interpretation && !interpreting && (
                <div style={{ textAlign: 'center', padding: '2rem', background: '#f5f3ff', borderRadius: 12, border: '1px solid #c4b5fd' }}>
                  <p style={{ fontSize: 13, color: '#7c3aed', margin: 0 }}>Click "Interpret Results" in the sidebar to generate doctoral-level interpretation of the real computed outputs.</p>
                </div>
              )}

              {(interpretation || interpreting) && (
                <div style={{ background: '#fff', border: '1px solid #DDE4EF', borderRadius: 12, padding: '1.5rem' }}>
                  {interpreting && !interpretation && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#7c3aed', marginBottom: 12 }}>
                      <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: 12 }}>Generating interpretation from real computed statistics…</span>
                    </div>
                  )}
                  <div style={{ fontSize: 14, lineHeight: 1.85, color: '#0f172a', whiteSpace: 'pre-wrap', fontFamily: '"Times New Roman", Georgia, serif' }}>
                    {interpretation}
                    {interpreting && <span style={{ animation: 'blink 1s step-end infinite', color: '#7c3aed' }}>|</span>}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

const selectStyle = {
  padding: '5px 6px', border: '1px solid #DDE4EF', borderRadius: 6,
  fontSize: 11, color: '#334155', background: '#fff', width: '100%',
}
